import React, { FC } from "react";

import { t } from "i18next";

import { EditableAvatar } from "@/components/molecules";

import { cn } from "@/utils/cn";

import { TCompany } from "@/types";

interface ICompanyBannerProps {
	company: TCompany;
	/** Owner only: the logo upload is refused for anyone else anyway. */
	canEdit: boolean;
	onLogoChange: (file: File) => Promise<unknown>;
}

/** Paints the stripe: the API answers `#000000` for a company with no colour. */
const DEFAULT_STRIPE = "#0383d3";

type TChip = { key: string; label: string; href?: string };

/**
 * The company's calling card, under the page title: its colour as a
 * stripe, its logo, and the contact details one wants without opening
 * a card — each a link where it can be (mailto/tel/website).
 */
export const CompanyBanner: FC<ICompanyBannerProps> = ({
	company,
	canEdit,
	onLogoChange,
}) => {
	const address = [
		company.address,
		[company.postalCode, company.city].filter(Boolean).join(" "),
	]
		.filter(Boolean)
		.join(", ");

	const chips: TChip[] = [
		company.email && {
			key: "email",
			label: company.email,
			href: `mailto:${company.email}`,
		},
		company.phone && {
			key: "phone",
			label: company.phone,
			href: `tel:${company.phone}`,
		},
		company.website && {
			key: "website",
			label: company.website.replace(/^https?:\/\//, ""),
			href: company.website,
		},
		address && { key: "address", label: address },
	].filter((chip): chip is TChip => !!chip);

	const chipClassName =
		"inline-flex items-center rounded-lg bg-neutral-100 px-2.5 py-1 text-xs text-neutral-800";

	return (
		<div
			data-testid="company-banner"
			className="overflow-hidden rounded-xl border border-neutral-200 bg-white"
		>
			<div
				data-testid="company-stripe"
				className="h-1.5"
				style={{
					backgroundColor:
						company.color && company.color !== "#000000"
							? company.color
							: DEFAULT_STRIPE,
				}}
			/>
			<div className="flex flex-wrap items-center gap-5 p-4">
				<EditableAvatar
					rounded={false}
					url={company.logo}
					onChange={onLogoChange}
					disabled={!canEdit}
					className="h-22 w-22 rounded-xl shadow-none"
					imageFit="contain"
				/>
				<div className="flex min-w-0 flex-1 flex-col gap-2">
					{chips.length > 0 ? (
						<div className="flex flex-wrap gap-2">
							{chips.map((chip) =>
								chip.href ? (
									<a
										key={chip.key}
										href={chip.href}
										target={
											chip.key === "website"
												? "_blank"
												: undefined
										}
										rel={
											chip.key === "website"
												? "noreferrer"
												: undefined
										}
										className={cn([
											chipClassName,
											"hover:bg-neutral-200",
										])}
									>
										{chip.label}
									</a>
								) : (
									<span
										key={chip.key}
										className={chipClassName}
									>
										{chip.label}
									</span>
								)
							)}
						</div>
					) : (
						<p className="text-sm text-neutral-500">
							{t("companies.banner.noContact")}
						</p>
					)}
				</div>
			</div>
		</div>
	);
};
